import { type ReactNode } from 'react';
import { useTheme } from '../../context/ThemeContext';

// Shared inline status banner (DESIGN_LANGUAGE_SPEC §25 error/empty states). Used to surface
// query/mutation failures and advisory notices in a Cortex-styled, theme-aware block. The C27
// review required mutation errors to be surfaced visibly rather than silently dropped, so the
// editor pages render their `mutationError` through `tone="error"` here.

export type BannerTone = 'error' | 'warning' | 'info';

const TONE: Record<BannerTone, { dark: string; light: string; darkText: string; lightText: string }> = {
  error: {
    dark: 'border border-rose-500/20 bg-rose-500/10',
    light: 'border border-rose-200 bg-rose-50',
    darkText: 'text-rose-300',
    lightText: 'text-rose-700',
  },
  warning: {
    dark: 'border border-amber-500/20 bg-amber-500/10',
    light: 'border border-amber-200 bg-amber-50',
    darkText: 'text-amber-200',
    lightText: 'text-amber-800',
  },
  info: {
    dark: 'border border-indigo-500/20 bg-indigo-500/10',
    light: 'border border-indigo-200 bg-indigo-50',
    darkText: 'text-indigo-200',
    lightText: 'text-indigo-700',
  },
};

export interface InlineBannerProps {
  tone: BannerTone;
  children: ReactNode;
  /** ARIA role — `alert` for errors (assertive), `status` for advisory notices. */
  role?: 'alert' | 'status' | undefined;
  testId?: string | undefined;
  icon?: ReactNode | undefined;
  className?: string | undefined;
}

export function InlineBanner({
  tone,
  children,
  role = tone === 'error' ? 'alert' : 'status',
  testId,
  icon,
  className,
}: InlineBannerProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const styles = TONE[tone];

  return (
    <div
      role={role}
      {...(role === 'status' ? { 'aria-live': 'polite' as const } : {})}
      data-testid={testId}
      className={`mb-6 flex items-start gap-3 rounded-xl p-4 ${
        isDark ? styles.dark : styles.light
      } ${className ?? ''}`}
    >
      {icon && (
        <span className={`mt-0.5 flex-shrink-0 ${isDark ? styles.darkText : styles.lightText}`}>
          {icon}
        </span>
      )}
      <p className={`text-sm ${isDark ? styles.darkText : styles.lightText}`}>{children}</p>
    </div>
  );
}
